/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext } from 'react';
// @ts-expect-error untyped package
import amplitude from 'amplitude-js';
import { Experience } from '@appcues/domain-types';
import type { AccountState } from 'ext/entities/account';
import {
  selectAccountId,
  selectEmail,
  selectIsSpoofing,
  selectUserId,
} from 'ext/entities/account';
import { createSegmentAnalyticsClient } from 'ext/lib/segment-analytics';

type PropertyValue = string | number | boolean | Experience | (() => unknown);

export interface BaseProperties {
  [key: string]: PropertyValue;
}

export interface TrackProperties {
  [key: string]: unknown;
}

interface FlattenedProperties {
  [key: string]: unknown;
  experienceId?: Experience['id'];
  experienceType?: Experience['type'];
  experience?: never;
}

export interface AnalyticsClient {
  page: () => void;
  track: (type: string, properties?: TrackProperties) => void;
}

const log =
  (prefix: string, client: string) =>
  (...args: unknown[]) =>
    // eslint-disable-next-line no-console
    console.log(`%c[${client}:${prefix}]`, 'color: #5c5cff', ...args);

export const createAnalyticsClient = <T extends { account: AccountState }>(
  state: T,
  baseProperties: BaseProperties = {},
  clientMode = false
): AnalyticsClient => {
  const amplitudeStub = {
    logEvent: log('logEvent', 'amplitude'),
  };

  const segmentClient = createSegmentAnalyticsClient(
    {
      userId: selectUserId(state),
      accountId: selectAccountId(state),
      isSpoofing: selectIsSpoofing(state),
      email: selectEmail(state),
    },
    baseProperties,
    clientMode
  );

  const shouldTrackEvent =
    !import.meta.env.DEBUG_LOGGER &&
    typeof import.meta.env.AMPLITUDE_API_KEY !== 'undefined' &&
    !selectIsSpoofing(state);

  if (shouldTrackEvent) {
    amplitude
      .getInstance()
      .init(import.meta.env.AMPLITUDE_API_KEY, selectUserId(state));
  }

  const amplitudeClient = shouldTrackEvent
    ? amplitude.getInstance()
    : amplitudeStub;

  // on client mode we need to group by userId
  const userId = selectUserId(state);
  const identifyProps = {
    identifyUserId: userId,
    traits: {
      env: import.meta.env.APPCUES_ENV,
      accountId: selectAccountId(state),
      email: selectEmail(state),
    },
  };

  segmentClient.identify(identifyProps);

  return {
    page: () => {
      segmentClient.page();
    },

    track: (type: string, properties: TrackProperties = {}) => {
      // Resolve any getter functions in baseProperties
      const resolvedBaseProperties = Object.entries(baseProperties).reduce(
        (acc, [key, value]) => {
          acc[key] = typeof value === 'function' ? value() : value;
          return acc;
        },
        {} as Record<string, unknown>
      );

      // Flatten experience object to experienceId and experienceType
      const flattenedProperties: FlattenedProperties = {
        ...resolvedBaseProperties,
      };
      if (
        flattenedProperties.experience &&
        typeof flattenedProperties.experience === 'object' &&
        'id' in flattenedProperties.experience &&
        'type' in flattenedProperties.experience
      ) {
        const { id, type: experienceType } =
          flattenedProperties.experience as Experience;
        flattenedProperties.experienceId = id;
        flattenedProperties.experienceType = experienceType;
        delete flattenedProperties.experience;
      }

      segmentClient.track({
        ...properties,
        ...flattenedProperties,
      });

      amplitudeClient.logEvent(type, {
        env: import.meta.env.APPCUES_ENV,
        accountId: selectAccountId(state),
        email: selectEmail(state),
        ...flattenedProperties,
        ...properties,
      });
    },
  };
};

const AnalyticsContext = createContext<AnalyticsClient>({
  page: () => {},
  track: () => {},
});

export default AnalyticsContext;
